import { ComponentProps, FC } from "react";
import { Stepper, Text } from "shared/components/Spor";
import { Indicator } from "shared/icons/Indicator";
import styled, { useTheme } from "styled-components";
/**
 * Future steps (step.index > activeStep) isn't packed inside a button
 * Thus in order to get consistent styling, we hide every button and
 * pack each step with its own button-like container called Step
 *
 * We also control some spor component's sizes.
 */
const StepperContainer = styled.div`
  button {
    padding: 0;
    margin: 0;
    background-color: transparent;
  }
  div > svg {
    /* Specifically target the '>' svg between steps */
    margin: 0px 12px;
  }
  p {
    padding: 0px;
  }
  &&& > * {
    /* Remove min-height from Spor */
    min-height: 0px;
  }
`;

// Button-like container
export const Step = styled.span<{ $active: boolean }>`
  background-color: ${({ $active, theme }) =>
    $active ? theme.colorSelected : undefined};
  padding: 3px 12px;
  border-radius: 30px;
  height: 36px;

  /* Using HStack leads to invalidDOMNesting, therefore I use a span styled as HStack (Spor workaround) */
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * Drops variant of stepper (See ProductionDeviation).
 * Steps are 0-indexed here instead in order to make it easier to use.
 *
 * Is just an alternative/updated styling. Does not fully replace stepper.
 */
export const DropsStepper: FC<ComponentProps<typeof Stepper>> = ({
  activeStep,
  steps,
  onClick,
  ...props
}) => {
  const theme = useTheme();
  const stepsWithIcon = steps.map(
    (it, index) =>
      (
        <Step $active={activeStep === index} key={`step-${it}}`}>
          <Indicator checked={index < activeStep} number={index + 1} />
          <Text
            style={{ fontSize: "18px" }}
            as="span"
            fontWeight={activeStep === index ? 700 : 400}
            color={
              index > activeStep ? theme.colorTextTertiary : theme.colorTextMain
            }
          >
            {it}
          </Text>
        </Step>
      ) as unknown as string, // Trick spor
  );
  return (
    <StepperContainer>
      <Stepper
        activeStep={activeStep + 1} // + 1 since we are working with 0-index
        steps={stepsWithIcon}
        onClick={(index) => onClick(index - 1)} // - 1 since we are working with 0-index
        {...props}
      />
    </StepperContainer>
  );
};
